import React from 'react';
import styled from 'styled-components';
import { theme } from '../styles/GlobalStyles';

const PageContainer = styled.div`
  padding-top: 120px; /* Account for standard fixed navbar */
  min-height: 100vh;
  background: ${theme.colors.background};
`;

const PageHero = styled.section`
  background: linear-gradient(135deg, rgba(40,82,111,0.9), rgba(64,120,170,0.8)),
              url('/img_to_use/about.jpg') center/cover no-repeat;
  padding: 4rem 0;
  text-align: center;
  color: white;
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const PageTitle = styled.h1`
  font-family: 'Playfair Display', serif;
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 900;
  margin-bottom: 1rem;
  text-shadow: 0 2px 4px rgba(0,0,0,0.3);
`;

const PageSubtitle = styled.p`
  font-size: clamp(1.1rem, 2vw, 1.3rem);
  opacity: 0.95;
  max-width: 600px;
  margin: 0 auto;
`;

const ContentSection = styled.section`
  max-width: 1000px;
  margin: 0 auto;
  padding: 4rem 2rem;
  line-height: 1.8;
`;

const SectionTitle = styled.h2`
  font-family: 'Playfair Display', serif;
  font-size: 2rem;
  color: ${theme.colors.primary};
  margin-bottom: 1.5rem;
  margin-top: 3rem;
  
  &:first-child {
    margin-top: 0;
  }
`;

const SubsectionTitle = styled.h3`
  font-family: 'Playfair Display', serif;
  font-size: 1.5rem;
  color: ${theme.colors.text};
  margin-bottom: 1rem;
  margin-top: 2rem;
`;

const Paragraph = styled.p`
  margin-bottom: 1.5rem;
  color: ${theme.colors.text};
  font-size: 1rem;
`;

const BulletList = styled.ul`
  margin-bottom: 1.5rem;
  padding-left: 2rem;
  
  li {
    margin-bottom: 0.5rem;
    color: ${theme.colors.text};
  }
`;

const HighlightBox = styled.div`
  background: ${theme.colors.sectionAlt};
  padding: 2rem;
  border-radius: 12px;
  border-left: 4px solid ${theme.colors.primary};
  margin: 2rem 0;
  
  h4 {
    font-family: 'Playfair Display', serif;
    color: ${theme.colors.primary};
    margin-bottom: 1rem;
  }
  
  p {
    margin-bottom: 0.5rem;
  }
`;

const ContactInfo = styled.div`
  background: ${theme.colors.sectionAlt};
  padding: 2rem;
  border-radius: 12px;
  border-left: 4px solid ${theme.colors.primary};
  margin: 2rem 0;
  
  h4 {
    font-family: 'Playfair Display', serif;
    color: ${theme.colors.primary};
    margin-bottom: 1rem;
  }
  
  p {
    margin-bottom: 0.5rem;
  }
`;

const LastUpdated = styled.div`
  font-style: italic;
  color: ${theme.colors.text};
  opacity: 0.7;
  margin-bottom: 2rem;
  text-align: center;
`;

const PrivacyPolicyPage: React.FC = () => {
  return (
    <PageContainer>
      <PageHero>
        <HeroContent>
          <PageTitle>Privacy Policy</PageTitle>
          <PageSubtitle>
            Your privacy matters to us. Learn how we protect and handle your personal information.
          </PageSubtitle>
        </HeroContent>
      </PageHero>
      
      <ContentSection>
        <LastUpdated>
          Last updated: {new Date().toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })}
        </LastUpdated>

        <SectionTitle>1. Introduction</SectionTitle>
        <Paragraph>
          De ARC Global LLC protects your privacy. This policy explains how we collect, use, and safeguard your information.
        </Paragraph>

        <SectionTitle>2. Information We Collect</SectionTitle>
        
        <SubsectionTitle>2.1 Personal Information</SubsectionTitle>
        <Paragraph>
          We may collect personal information that you voluntarily provide to us when you:
        </Paragraph>
        <BulletList>
          <li>Contact us through our website or forms</li>
          <li>Subscribe to our newsletters or notifications</li>
          <li>Apply for job opportunities</li>
          <li>Opt in to receive SMS/text messages</li>
          <li>Use our services or engage with our platform</li>
        </BulletList>
        <Paragraph>
          This information may include: name, email address, phone number, address, resume/CV, employment history, and other relevant professional information.
        </Paragraph>

        <SubsectionTitle>2.2 Automatically Collected Information</SubsectionTitle>
        <Paragraph>
          When you visit our website, we may automatically collect certain information about your device and usage patterns, including IP address, browser type, operating system, referring URLs, and pages viewed.
        </Paragraph>

        <SectionTitle>3. SMS Consent and Text Messaging</SectionTitle>
        
        <HighlightBox>
          <h4>SMS Consent</h4>
          <p>
            <strong>We will not share your opt-in to an SMS campaign with any third party for purposes unrelated to providing you with the services of that campaign.</strong> We may share your Personal Data, including your SMS opt-in or consent status, with third parties that help us provide our messaging services.
          </p>
          <p>
            <strong>All of the above categories exclude text messaging originator opt-in data and consent; this information will not be shared with any third parties.</strong>
          </p>
        </HighlightBox>

        <SubsectionTitle>3.1 How We Use SMS Information</SubsectionTitle>
        <Paragraph>
          When you opt in to receive text messages from us, we use your mobile phone number to send:
        </Paragraph>
        <BulletList>
          <li>Appointment reminders and confirmations</li>
          <li>Important updates about our services</li>
          <li>Job opportunity notifications (if requested)</li>
          <li>Other business-related communications you've consented to receive</li>
        </BulletList>

        <SubsectionTitle>3.2 Message Frequency and Charges</SubsectionTitle>
        <Paragraph>
          Message frequency varies based on your interactions and preferences. Standard message and data rates apply as determined by your mobile carrier.
        </Paragraph>

        <HighlightBox>
          <h4>Opt-Out Process</h4>
          <p>
            <strong>You may choose to stop receiving text messages from De ARC Global LLC at any time.</strong> To opt out, simply reply to any text message or send a message with the word 'STOP' or 'UNSUBSCRIBE' to our number. We will promptly remove your number from our messaging list.
          </p>
          <p>
            You may also contact us directly using the contact information provided at the end of this policy to opt out of SMS communications.
          </p>
        </HighlightBox>

        <SectionTitle>4. How We Use Your Information</SectionTitle>
        <Paragraph>
          We use the information we collect to:
        </Paragraph>
        <BulletList>
          <li>Provide and maintain our services</li>
          <li>Process job applications and recruitment activities</li>
          <li>Communicate with you about our services</li>
          <li>Send you marketing communications (with your consent)</li>
          <li>Improve our website and services</li>
          <li>Comply with legal obligations</li>
          <li>Protect our rights and prevent fraud</li>
        </BulletList>

        <SectionTitle>5. Information Sharing and Disclosure</SectionTitle>
        <Paragraph>
          We do not sell, trade, or otherwise transfer your personal information to third parties except in the following circumstances:
        </Paragraph>
        <BulletList>
          <li><strong>Service Providers:</strong> We may share information with trusted third-party service providers who assist us in operating our website and conducting our business</li>
          <li><strong>Client Companies:</strong> For recruitment purposes, we may share relevant professional information with potential employers (with your consent)</li>
          <li><strong>Legal Compliance:</strong> When required by law or to protect our rights and safety</li>
          <li><strong>Business Transfers:</strong> In connection with any merger, sale, or transfer of company assets</li>
        </BulletList>

        <SectionTitle>6. Data Security</SectionTitle>
        <Paragraph>
          We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet or electronic storage is 100% secure.
        </Paragraph>

        <SectionTitle>7. Data Retention</SectionTitle>
        <Paragraph>
          We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law.
        </Paragraph>

        <SectionTitle>8. Your Rights and Choices</SectionTitle>
        <Paragraph>
          Depending on your location, you may have certain rights regarding your personal information, including:
        </Paragraph>
        <BulletList>
          <li>The right to access your personal information</li>
          <li>The right to correct or update your information</li>
          <li>The right to delete your information (subject to legal requirements)</li>
          <li>The right to opt out of marketing communications</li>
          <li>The right to withdraw consent for SMS messaging</li>
        </BulletList>

        <SectionTitle>9. Cookies and Tracking Technologies</SectionTitle>
        <Paragraph>
          Our website may use cookies and similar tracking technologies to enhance user experience and analyze website usage. You can control cookie preferences through your browser settings.
        </Paragraph>

        <SectionTitle>10. Children's Privacy</SectionTitle>
        <Paragraph>
          Our services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children under 18. If we become aware that we have collected such information, we will take steps to delete it.
        </Paragraph>

        <SectionTitle>11. International Data Transfers</SectionTitle>
        <Paragraph>
          Your information may be transferred to and processed in countries other than your country of residence. We ensure appropriate safeguards are in place for such transfers.
        </Paragraph>

        <SectionTitle>12. Changes to This Privacy Policy</SectionTitle>
        <Paragraph>
          We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
        </Paragraph>

        <SectionTitle>13. Contact Us</SectionTitle>
        <Paragraph>
          If you have any questions about this Privacy Policy or our privacy practices, please contact us:
        </Paragraph>

        <ContactInfo>
          <h4>Contact Information</h4>
          <p><strong>De ARC Global LLC</strong></p>
          <p>Canton, Michigan 48186</p>
          <p>United States</p>
          <p>Phone: +1 734-560-2886</p>
          <p>Email: info@dearcglobal.com</p>
        </ContactInfo>

        <HighlightBox>
          <h4>SMS Support</h4>
          <p>For specific questions about our SMS services:</p>
          <p>• Reply "HELP" to any text message</p>
          <p>• Reply "STOP" to unsubscribe</p>
          <p>• Contact us using the information above</p>
        </HighlightBox>
      </ContentSection>
    </PageContainer>
  );
};

export default PrivacyPolicyPage;
